import { CSSProperties, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import useBoundStore from "store";

interface VirtualizedListProps<T> {
  estimateSize: number;
  height?: string | number;
  rows: T;
  style?: CSSProperties;
}

export default function VirtualizedList<T extends JSX.Element[]>(props: VirtualizedListProps<T>): JSX.Element {
  const { estimateSize, height, rows, style } = props;
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    // This is used only for testing purpose. A better solution should be found
    overscan: process.env.NODE_ENV === "test" ? 100 : undefined,
  });

  return (
    <div
      data-testid='virtualized-list'
      ref={parentRef}
      style={{
        ...style,
        height: height ?? `calc(100vh - ${isMobileDevice ? "56px" : "96px"}`,
        overflow: "auto",
        paddingBottom: "2.25rem",
        width: "100%",
      }}
    >
      <div
        role='list'
        style={{
          height: rowVirtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={rowVirtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <div>{rows[virtualRow.index]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
